import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from '@mantine/hooks';
import type { ViewStateChangeParameters } from '@deck.gl/core';

const ZOOM_PRECISION = 2;
const COORD_PRECISION = 4;

/**
 * Builds a `URLSearchParams` with `zoom`, `lat`, `lng` set to the given view
 * state, preserving any other params passed in `base`. Single source of truth
 * for the URL form consumed by `parseUrlViewState`.
 */
export const buildViewStateSearchParams = (
  vs: { lat: number; lng: number; zoom: number },
  base?: URLSearchParams
): URLSearchParams => {
  const params = new URLSearchParams(base);
  params.set('zoom', vs.zoom.toFixed(ZOOM_PRECISION));
  params.set('lat', vs.lat.toFixed(COORD_PRECISION));
  params.set('lng', vs.lng.toFixed(COORD_PRECISION));
  return params;
};

const isUserGesture = ({
  isDragging,
  isPanning,
  isZooming,
  isRotating,
  inTransition,
}: ViewStateChangeParameters['interactionState']) =>
  !inTransition && !!(isDragging || isPanning || isZooming || isRotating);

/**
 * Returns a DeckGL `onViewStateChange` handler that debounces writes of
 * `zoom`, `lat`, and `lng` to the URL. Only fires for user interactions.
 * Programmatic changes (initial mount, resize, flyTo, etc.) are skipped.
 */
export const useViewStateUrlSync = () => {
  const [, setSearchParams] = useSearchParams();
  const writeUrl = useDebouncedCallback(
    (vs: { zoom: number; latitude: number; longitude: number }) => {
      setSearchParams(
        (prev) =>
          buildViewStateSearchParams(
            { lat: vs.latitude, lng: vs.longitude, zoom: vs.zoom },
            prev
          ),
        { replace: true }
      );
    },
    { delay: 1000, flushOnUnmount: true }
  );

  return useCallback(
    ({ viewState, interactionState }: ViewStateChangeParameters) => {
      if (isUserGesture(interactionState)) {
        writeUrl(viewState);
      }
    },
    [writeUrl]
  );
};
